import { apiClient } from '@/auth';

/**
 * Per swagger.yaml:15614 the endpoint returns `{patient_id, date_since,
 * date_until, metrics: [...]}`, not a bare array. V2 used to assume the
 * latter and crashed with `rows.filter is not a function` on real data.
 */

export type CarePlanDailyValue = {
  log_date: string;
  value: number | null;
  target_achieved?: boolean | null;
  target_range?: Array<number | null> | null;
  status: 'met' | 'not_met' | 'no_target' | 'unknown';
};

export type CarePlanTarget = {
  kind: 'standard' | 'patient' | 'individualized';
  source: string;
  range_type?: 'above' | 'below' | 'within' | null;
  target_range?: Array<number | null> | null;
  requires_hcp_supervision: boolean;
  note?: string | null;
};

export type CarePlanValue = {
  id: string;
  domain: string;
  priority_tier: 1 | 2 | 3 | 4 | 5;
  target?: CarePlanTarget | null;
  values: CarePlanDailyValue[];
};

type CarePlanValuesResponse = {
  patient_id: number;
  date_since: string;
  date_until: string;
  metrics: CarePlanValue[];
};

/**
 * One nutrient row inside an RdiResponse. The endpoint returns each nutrient
 * keyed on its BASE name ("calcium", "iron", "vitamin b12") — call sites
 * have to match against the suffixed table fields ("calcium_mg", etc.).
 * `value_low` / `value_high` are the recommended target band; either may be
 * null for "no target" nutrients. Per MNT swagger:17572.
 */
export type RdiNutrient = {
  nutrient: string;
  unit: string;
  classification: 'daily_rda' | 'daily_ul' | 'n/a';
  value_low?: number | null;
  value_high?: number | null;
};

/** GET /patients/{id}/rdi response item. Per MNT swagger:17560. */
export type RdiResponse = {
  age: number;
  country: 'Canada' | 'USA';
  sex: string;
  nutrients: RdiNutrient[];
};

export const nutrientsApi = {
  async carePlanValues(patientId: number, params?: { from?: string; to?: string }): Promise<CarePlanValue[]> {
    const res = await apiClient.get<CarePlanValuesResponse>(`/patients/${patientId}/care-plan/values`, {
      params: {
        date_since: params?.from,
        date_until: params?.to,
      },
    });
    return Array.isArray(res.data?.metrics) ? res.data.metrics : [];
  },
  /**
   * Recommended Daily Intake band for the patient. Server returns an ARRAY
   * (v1 noticed this and used `rdiData[0]` defensively). v2 normalizes —
   * always returns the first element or null so callers don't have to guard.
   */
  async rdi(patientId: number): Promise<RdiResponse | null> {
    try {
      const res = await apiClient.get<RdiResponse | RdiResponse[]>(`/patients/${patientId}/rdi`);
      if (Array.isArray(res.data)) return res.data[0] ?? null;
      return res.data ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return null;
      throw err;
    }
  },
  async nftSummary(patientId: number) {
    const res = await apiClient.get(`/patients/${patientId}/nft-summary`);
    return res.data;
  },
};
